import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CategoriesPieChart from "./CategoriesPieChart";
import * as XLSX from "xlsx";
import Navbar from "./Navbar";

const API_BASE_URL = "https://prodapi.iworkkz.com:9095";
const API_KEY = "X4gfacRza++DrL%2FqFYkhZJAuiuuJEsGwmfJz7tZHGi4%3D";

/* flattenRow, normalizePayload, toSummaryFromRaw, timestampForFile, exportToExcel
   remain the same as in your current file – keep them here (omitted for brevity).
   Below is the full component with filtering support.
*/

function flattenRow(row) {
  const out = {};
  if (!row || typeof row !== "object") return { value: String(row) };
  Object.keys(row).forEach((k) => {
    const v = row[k];
    if (v === null || v === undefined) out[k] = "";
    else if (typeof v === "object") {
      if (Array.isArray(v)) out[k] = v.join(", ");
      else out[k] = JSON.stringify(v);
    } else out[k] = v;
  });
  return out;
}

function normalizePayload(payload, expectedFieldName) {
  if (
    Array.isArray(payload) &&
    payload.length &&
    payload[0].name !== undefined &&
    payload[0].value !== undefined
  ) {
    return { summary: payload, rows: [] };
  }
  if (payload && payload.summary && Array.isArray(payload.summary)) {
    return { summary: payload.summary, rows: payload.raw || [] };
  }
  if (Array.isArray(payload)) {
    const rows = payload;
    // basic aggregator
    const counts = rows.reduce((acc, r) => {
      const raw = (r[expectedFieldName] || "").toString();
      if (!raw) return acc;
      raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((cat) => (acc[cat] = (acc[cat] || 0) + 1));
      return acc;
    }, {});
    const summary = Object.keys(counts).map((k) => ({
      name: k,
      value: counts[k],
    }));
    return { summary, rows };
  }
  return { summary: [], rows: [] };
}

function timestampForFile() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(
    d.getHours()
  )}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

export default function Landing() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState([]);
  const [purSummary, setPurSummary] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // filtersg
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPurpose, setSelectedPurpose] = useState(null);

  const [showPopup, setShowPopup] = useState(false);
  const [popupImages, setPopupImages] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    setError(null);

    try {
      // categories summary
      let res = await fetch(API_BASE_URL + "/api/Dashboard/getCategories");
      if (!res.ok) throw new Error(`Categories API HTTP ${res.status}`);
      let payload = await res.json();
      let { summary: catSummary } = normalizePayload(payload, "CATEGORIES");
      setSummary(catSummary);

      // purpose summary
      res = await fetch(API_BASE_URL + "/api/Dashboard/getPurpose");
      if (!res.ok) throw new Error(`Purpose API HTTP ${res.status}`);
      payload = await res.json();
      let { summary: pSummary } = normalizePayload(payload, "PURPOSE");
      setPurSummary(pSummary);

      // leads
      res = await fetch(API_BASE_URL + "/api/Dashboard/getListOfLeads");
      if (!res.ok) throw new Error(`Leads API HTTP ${res.status}`);
      const leadsPayload = await res.json();
      let leadRows = [];
      if (Array.isArray(leadsPayload)) leadRows = leadsPayload;
      else if (leadsPayload && Array.isArray(leadsPayload.rows))
        leadRows = leadsPayload.rows;
      else if (leadsPayload && Array.isArray(leadsPayload.Leads))
        leadRows = leadsPayload.Leads;
      else {
        const arrProp = Object.values(leadsPayload || {}).find((v) =>
          Array.isArray(v)
        );
        if (arrProp) leadRows = arrProp;
      }
      setLeads(Array.isArray(leadRows) ? leadRows : []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  // columns
  const leadColumns = useMemo(() => {
    if (!leads || leads.length === 0) return [];
    const set = new Set();
    leads.forEach((r) => {
      if (r && typeof r === "object") Object.keys(r).forEach((k) => set.add(k));
    });
    return Array.from(set);
  }, [leads]);

  // filtered leads based on selectedCategory / selectedPurpose
  const filteredLeads = useMemo(() => {
    if ((!selectedCategory && !selectedPurpose) || !leads) return leads;
    return leads.filter((row) => {
      let ok = true;
      if (selectedCategory) {
        const cats = (row.CATEGORIES || row.categories || row.Cat || "")
          .toString()
          .toLowerCase();
        ok = ok && cats.includes(selectedCategory.toLowerCase());
      }
      if (selectedPurpose) {
        const pur = (row.PURPOSE || row.purpose || "").toString().toLowerCase();
        ok = ok && pur.includes(selectedPurpose.toLowerCase());
      }
      return ok;
    });
  }, [leads, selectedCategory, selectedPurpose]);

  // export (using flattened rows)
  function exportToExcel() {
    if (!filteredLeads || !filteredLeads.length) return;
    const prepared = filteredLeads.map(flattenRow);
    const ws = XLSX.utils.json_to_sheet(prepared, { skipHeader: false });
    const cols = leadColumns.length
      ? leadColumns
      : Object.keys(prepared[0] || {});
    ws["!cols"] = cols.map((c) => {
      let max = c.length;
      prepared.forEach((r) => {
        const val = r[c] === undefined || r[c] === null ? "" : String(r[c]);
        if (val.length > max) max = val.length;
      });
      return { wch: Math.min(Math.max(max + 2, 10), 50) };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    XLSX.writeFile(wb, `leads_${timestampForFile()}.xlsx`);
  }

  async function getEnquiryImages(enquiryId) {
    const res = await fetch(
      `${API_BASE_URL}/api/dashboard/getEnquiryImages?EnquiryId=${enquiryId}`
    );
    if (!res.ok) throw new Error("Failed to load images");
    return await res.json();
  }
  const openAttachmentPopup = async (enquiryId) => {
    try {
      setPopupLoading(true);
      const images = await getEnquiryImages(enquiryId);
      setPopupImages(images || []);
      setShowPopup(true);
    } catch (err) {
      alert("No images found for this enquiry");
    } finally {
      setPopupLoading(false);
    }
  };

  function downloadBase64File(base64Data, fileName) {
    try {
      if (!base64Data) {
        alert("Image data missing");
        return;
      }

      // ✅ Fallback filename
      const safeFileName =
        fileName && fileName.trim() !== ""
          ? fileName
          : `image_${Date.now()}.jpg`;

      // ✅ Detect MIME type from file extension
      let mimeType = "image/jpeg";
      if (safeFileName.toLowerCase().endsWith(".png")) mimeType = "image/png";
      if (safeFileName.toLowerCase().endsWith(".webp")) mimeType = "image/webp";

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = safeFileName;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);

      // ✅ Delay revoke to avoid Chrome failure
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (err) {
      console.error("DOWNLOAD ERROR:", err);
      alert("Download failed due to browser security");
    }
  }

  if (loading) return <div className="p-3">Loading...</div>;
  if (error) return <div className="p-3 text-danger">Error: {error}</div>;

  return (
    <>
      <Navbar />

      <div className="page-content">
        <div className="container mt-4">
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <h6 className="card-title mb-0 me-auto">
                      Category Summary
                    </h6>
                    {selectedCategory && (
                      <span className="badge bg-secondary me-2">
                        Filter: {selectedCategory}
                      </span>
                    )}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSelectedCategory(null)}
                    >
                      Clear Category
                    </button>
                  </div>
                  <CategoriesPieChart
                    data={summary}
                    onSelect={(name) => setSelectedCategory(name)}
                    selected={selectedCategory}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <h6 className="card-title mb-0 me-auto">Purpose Summary</h6>
                    {selectedPurpose && (
                      <span className="badge bg-secondary me-2">
                        Filter: {selectedPurpose}
                      </span>
                    )}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSelectedPurpose(null)}
                    >
                      Clear Purpose
                    </button>
                  </div>
                  <CategoriesPieChart
                    data={purSummary}
                    onSelect={(name) => setSelectedPurpose(name)}
                    selected={selectedPurpose}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mt-4">
          <div className="row g-3">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <h6 className="card-title mb-0 me-auto">Leads</h6>
                    <div className="me-2">
                      <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => {
                          setSelectedCategory(null);
                          setSelectedPurpose(null);
                        }}
                      >
                        Clear All
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={exportToExcel}
                        disabled={!filteredLeads || filteredLeads.length === 0}
                      >
                        Download Excel
                      </button>
                    </div>
                  </div>

                  {!filteredLeads || filteredLeads.length === 0 ? (
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>
                              <span className="placeholder col-8"></span>
                            </th>
                            <th>
                              <span className="placeholder col-6"></span>
                            </th>
                            <th>
                              <span className="placeholder col-4"></span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <span className="placeholder col-10"></span>
                            </td>
                            <td>
                              <span className="placeholder col-8"></span>
                            </td>
                            <td>
                              <span className="placeholder col-6"></span>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <span className="placeholder col-9"></span>
                            </td>
                            <td>
                              <span className="placeholder col-7"></span>
                            </td>
                            <td>
                              <span className="placeholder col-5"></span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered">
                        <thead>
                          <tr>
                            {leadColumns.map((c) => (
                              <th key={c}>{c}</th>
                            ))}
                            <th className="text-center">Attachment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLeads.map((row, i) => (
                            <tr key={i}>
                              {leadColumns.map((col) => {
                                const cell = row[col];
                                const display =
                                  cell === null || cell === undefined
                                    ? ""
                                    : typeof cell === "object"
                                    ? Array.isArray(cell)
                                      ? cell.join(", ")
                                      : JSON.stringify(cell)
                                    : String(cell);
                                return <td key={col}>{display}</td>;
                              })}

                              {/* ✅ ATTACHMENT COLUMN */}
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-outline-dark"
                                  onClick={() =>
                                    openAttachmentPopup(
                                      row.Id || row.ID || row.id
                                    )
                                  }
                                >
                                  👁️
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showPopup && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: 20,
                borderRadius: 12,
                width: "90%",
                maxWidth: "1200px",
                maxHeight: "85vh",
                overflowY: "auto",
              }}
            >
              {/* HEADER */}
              <div className="d-flex justify-content-between mb-3">
                <h5>Attachments</h5>
                <button
                  onClick={() => setShowPopup(false)}
                  className="btn btn-sm btn-danger"
                >
                  ✖
                </button>
              </div>

              {popupLoading ? (
                <div className="text-center p-4">Loading images...</div>
              ) : popupImages.length === 0 ? (
                <div className="text-center text-muted">No images found</div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      window.innerWidth <= 768 ? "1fr" : "repeat(5, 1fr)",
                    gap: 12,
                  }}
                >
                  {Array.from({ length: 5 }).map((_, index) => {
                    const img = popupImages[index]; // may be undefined

                    return (
                      <div
                        key={index}
                        style={{
                          textAlign: "center",
                          border: "1px dashed #aaa",
                          borderRadius: 8,
                          padding: 8,
                          minHeight: 320,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        {img ? (
                          <>
                            <img
                              src={`data:image/jpeg;base64,${img.Base64Data}`}
                              alt={img.FileName}
                              style={{
                                width: "100%",
                                height: 240,
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />

                            <button
                              className="btn btn-sm btn-success mt-2"
                              onClick={() =>
                                downloadBase64File(img.Base64Data, img.FileName)
                              }
                            >
                              ⬇ Download
                            </button>
                          </>
                        ) : (
                          <>
                            {/* ✅ EMPTY SLOT */}
                            <div
                              style={{
                                width: "100%",
                                height: 180,
                                background: "#f1f1f1",
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#888",
                                fontSize: 14,
                                fontWeight: "bold",
                              }}
                            >
                              No Image
                            </div>

                            <div
                              style={{
                                fontSize: 12,
                                marginTop: 6,
                                color: "#aaa",
                              }}
                            >
                              Empty Slot
                            </div>

                            <button
                              className="btn btn-sm btn-secondary mt-2"
                              disabled
                            >
                              ⬇ Download
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
